import React from 'react';
import { MINT_STAGES } from '../hooks/useMint';
import { MINT_PRICE_ETH, CONTRACT_ADDRESS } from '../constants';

const STAGE_MESSAGES = {
  [MINT_STAGES.IDLE]: `Mint with Wallet (${MINT_PRICE_ETH} ETH)`,
  [MINT_STAGES.CAPTURING]: 'Capturing ticket...',
  [MINT_STAGES.UPLOADING_IMAGE]: 'Uploading image to IPFS...',
  [MINT_STAGES.UPLOADING_METADATA]: 'Uploading metadata...',
  [MINT_STAGES.AWAITING_APPROVAL]: 'Confirm in wallet...',
  [MINT_STAGES.CONFIRMING]: 'Confirming transaction...',
  [MINT_STAGES.SUCCESS]: 'Minted!',
  [MINT_STAGES.ERROR]: 'Try again',
};

const MintButton = ({
  stage,
  connected,
  isOnBase,
  txHash,
  tokenId,
  error,
  onMint,
  onReset,
  flightData,
  ticketRef
}) => {

  const isIdle = stage === MINT_STAGES.IDLE;
  const isSuccess = stage === MINT_STAGES.SUCCESS;
  const isError = stage === MINT_STAGES.ERROR;
  const isMinting = !isIdle && !isSuccess && !isError;

  const canMint = connected && isOnBase && isIdle;

  const handleWalletMint = () => {
    if (isSuccess || isError) {
      onReset();
    } else if (canMint) {
      onMint();
    }
  };

  // Progress indicator
  const getProgress = () => {
    const stages = [
      MINT_STAGES.CAPTURING,
      MINT_STAGES.UPLOADING_IMAGE,
      MINT_STAGES.UPLOADING_METADATA,
      MINT_STAGES.AWAITING_APPROVAL,
      MINT_STAGES.CONFIRMING,
    ];
    const index = stages.indexOf(stage);
    return index >= 0 ? ((index + 1) / stages.length) * 100 : 0;
  };

  return (
    <div>
      {/* Wallet-Based Minting Button */}
        <button
          onClick={handleWalletMint}
          disabled={!canMint && !isSuccess && !isError}
          style={{
            width: '100%',
            padding: '16px 24px',
            borderRadius: '12px',
            border: 'none',
            fontWeight: '600',
            fontSize: '16px',
            cursor: canMint || isSuccess || isError ? 'pointer' : 'not-allowed',
            background: isSuccess
              ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
              : isError
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : canMint
              ? 'linear-gradient(135deg, #0033A0 0%, #004494 100%)'
              : 'rgba(255,255,255,0.1)',
            color: canMint || isSuccess || isError ? '#ffffff' : 'rgba(255,255,255,0.4)',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.2s ease',
            boxShadow: canMint ? '0 4px 20px rgba(0, 51, 160, 0.3)' : 'none',
          }}
        >
          {/* Progress bar */}
          {isMinting && (
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              height: '3px',
              width: `${getProgress()}%`,
              background: 'rgba(255,255,255,0.5)',
              transition: 'width 0.3s ease',
            }} />
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {isMinting && (
              <span style={{
                display: 'inline-block',
                animation: 'spin 1s linear infinite',
              }}>
                ⏳
              </span>
            )}
            {isSuccess && <span>🎉</span>}
            {isError && <span>⚠️</span>}
            {!connected && '🔗 Connect wallet to mint'}
            {connected && !isOnBase && '⚠️ Switch to Base network'}
            {connected && isOnBase && STAGE_MESSAGES[stage]}
          </div>
        </button>

      {/* Success details */}
      {isSuccess && txHash && (
        <div style={{
          marginTop: '16px',
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          borderRadius: '12px',
          padding: '16px',
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#22c55e',
            marginBottom: '12px',
          }}>
            🎉 NFT Minted Successfully!
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <a
              href={`https://basescan.org/tx/${txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#60a5fa',
                fontSize: '13px',
                textDecoration: 'none',
              }}
            >
              View transaction on Basescan →
            </a>
            {tokenId && (
              <a
                href={`https://opensea.io/assets/base/${CONTRACT_ADDRESS}/${tokenId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#60a5fa',
                  fontSize: '13px',
                  textDecoration: 'none',
                }}
              >
                View on OpenSea →
              </a>
            )}
          </div>

          {/* Share buttons */}
          {tokenId && (
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(34, 197, 94, 0.2)' }}>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '10px' }}>
                Share your flight NFT
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    const openSeaUrl = `https://opensea.io/assets/base/${CONTRACT_ADDRESS}/${tokenId}`;
                    const tweetText = `Just minted my flight as an NFT! ✈️\n\nCheck it out on @OpenSea 👇\n${openSeaUrl}\n\n#UnitedAirlines #NFT #Aviation #Web3`;
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`, '_blank', 'width=600,height=400');
                  }}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'rgba(29, 161, 242, 0.2)',
                    color: '#1DA1F2',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  Share on X
                </button>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(`https://opensea.io/assets/base/${CONTRACT_ADDRESS}/${tokenId}`);
                      // Could add a toast here
                    } catch (err) {
                      console.error('Failed to copy:', err);
                    }
                  }}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'rgba(255,255,255,0.1)',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copy Link
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error details */}
      {isError && error && (
        <div style={{
          marginTop: '16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '12px',
          padding: '16px',
        }}>
          <div style={{
            fontSize: '13px',
            color: '#ef4444',
            wordBreak: 'break-word',
          }}>
            {error}
          </div>
        </div>
      )}

      {/* Contract info */}
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '8px',
        fontSize: '12px',
        color: 'rgba(255,255,255,0.5)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <span>Contract</span>
          <span style={{ fontFamily: 'monospace' }}>
            {CONTRACT_ADDRESS.slice(0, 6)}...{CONTRACT_ADDRESS.slice(-4)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Network</span>
          <span>Base</span>
        </div>
        <a
          href={`https://basescan.org/address/${CONTRACT_ADDRESS}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#60a5fa',
            textDecoration: 'none',
            display: 'block',
            marginTop: '8px',
          }}
        >
          View contract →
        </a>
      </div>
    </div>
  );
};

export default MintButton;
